/**
 * User Form Dialog - Create/Edit User
 *
 * Soporta selección múltiple de sucursales (M2M).
 * Administrador Global no requiere asignación de sucursales.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronsUpDown, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { User, Role, Sucursal } from "@/types/user";
import { useApiError } from "@/hooks/use-api-error";
import { useSucursal, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { useCreateUser, useUpdateUser } from "@/hooks/use-users";

const ADMIN_GLOBAL_ROLE = "Administrador Global";

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  roles: Role[];
  sucursales: Sucursal[];
  onSuccess: (isNew: boolean) => void;
}

// Validation schema
const userSchema = z.object({
  email: z.string().email("usuarios.validation.invalidEmail"),
  nombre: z.string().min(2, "usuarios.validation.nameRequired"),
  apellido: z.string().min(2, "usuarios.validation.lastNameRequired"),
  password: z.string().optional(),
  role_id: z.string().optional(),
  sucursal_ids: z.array(z.string()),
});

type FormData = z.infer<typeof userSchema>;

export function UserFormDialog({
  open,
  onOpenChange,
  user,
  roles,
  sucursales,
  onSuccess,
}: UserFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { sucursalActiva, isAllSucursalesSelected, canViewAllSucursales } = useSucursal();

  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [sucursalPopoverOpen, setSucursalPopoverOpen] = useState(false);

  const isEditing = !!user;

  // Solo bloquear el selector si el usuario NO es Admin Global (canViewAllSucursales = false)
  // Un Admin Global siempre puede elegir cualquier sucursal, sin importar cuál tenga activa
  const isSucursalLocked =
    !canViewAllSucursales &&
    !isAllSucursalesSelected &&
    sucursalActiva &&
    sucursalActiva.id !== ALL_SUCURSALES_ID;

  const createUserMutation = useCreateUser({
    onSuccess: () => onSuccess(true),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateUserMutation = useUpdateUser({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createUserMutation.isPending || updateUserMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: "",
      nombre: "",
      apellido: "",
      password: "",
      role_id: "",
      sucursal_ids: [],
    },
  });

  // Reset form when dialog opens/closes or user changes
  useEffect(() => {
    if (open) {
      if (user) {
        reset({
          email: user.email,
          nombre: user.nombre,
          apellido: user.apellido,
          role_id: user.role?.id ? String(user.role.id) : "",
          sucursal_ids: user.sucursales?.map((s) => String(s.id)) || [],
        });
      } else {
        reset({
          email: "",
          nombre: "",
          apellido: "",
          password: "",
          role_id: "",
          sucursal_ids: isSucursalLocked && sucursalActiva ? [String(sucursalActiva.id)] : [],
        });
      }
      setError(null);
      setPasswordError(null);
      setSucursalPopoverOpen(false);
    }
  }, [open, user, reset, isSucursalLocked, sucursalActiva]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) return "usuarios.validation.passwordMin";
    if (!/[A-Z]/.test(password)) return "usuarios.validation.passwordUppercase";
    if (!/[0-9]/.test(password)) return "usuarios.validation.passwordNumber";
    return null;
  };

  const onSubmit = async (data: FormData) => {
    setError(null);
    setPasswordError(null);

    if (!isEditing) {
      const pwdError = validatePassword(data.password || "");
      if (pwdError) {
        setPasswordError(pwdError);
        return;
      }
    }

    const sucursalIds = data.sucursal_ids.map(Number);

    if (isEditing && user) {
      updateUserMutation.mutate({
        id: user.id,
        data: {
          email: data.email,
          nombre: data.nombre,
          apellido: data.apellido,
          role_id: data.role_id ? Number(data.role_id) : null,
          sucursal_ids: sucursalIds,
        },
      });
    } else {
      createUserMutation.mutate({
        email: data.email,
        nombre: data.nombre,
        apellido: data.apellido,
        password: data.password || "",
        role_id: data.role_id ? Number(data.role_id) : null,
        sucursal_ids: sucursalIds,
      });
    }
  };

  const roleValue = watch("role_id");
  const selectedSucursalIds = watch("sucursal_ids");

  // Check if selected role is Administrador Global
  const selectedRole = roles.find((r) => String(r.id) === roleValue);
  const isAdminGlobal = selectedRole?.name === ADMIN_GLOBAL_ROLE;

  const toggleSucursal = (sucursalId: string) => {
    const current = selectedSucursalIds || [];
    const next = current.includes(sucursalId)
      ? current.filter((id) => id !== sucursalId)
      : [...current, sucursalId];
    setValue("sucursal_ids", next);
  };

  // Get names of selected sucursales for display
  const selectedSucursalNames = sucursales
    .filter((s) => selectedSucursalIds?.includes(String(s.id)))
    .map((s) => s.nombre);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? t("usuarios.editUser") : t("usuarios.newUser")}</DialogTitle>
          <DialogDescription>
            {isEditing ? t("usuarios.editUserDesc") : t("usuarios.newUserDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{t("usuarios.fields.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="correo@ejemplo.com"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{t(errors.email.message as string)}</p>
              )}
            </div>

            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="nombre">{t("usuarios.fields.nombre")}</Label>
              <Input id="nombre" {...register("nombre")} />
              {errors.nombre && (
                <p className="text-sm text-destructive">{t(errors.nombre.message as string)}</p>
              )}
            </div>

            {/* Apellido */}
            <div className="space-y-2">
              <Label htmlFor="apellido">{t("usuarios.fields.apellido")}</Label>
              <Input id="apellido" {...register("apellido")} />
              {errors.apellido && (
                <p className="text-sm text-destructive">{t(errors.apellido.message as string)}</p>
              )}
            </div>

            {/* Password (only for create) */}
            {!isEditing && (
              <div className="space-y-2">
                <Label htmlFor="password">{t("usuarios.fields.password")}</Label>
                <Input id="password" type="password" {...register("password")} />
                {passwordError && <p className="text-sm text-destructive">{t(passwordError)}</p>}
                <p className="text-xs text-muted-foreground">{t("usuarios.passwordHint")}</p>
              </div>
            )}

            {/* Role */}
            <div className="space-y-2">
              <Label htmlFor="role">{t("usuarios.fields.role")}</Label>
              <Select
                value={roleValue || "none"}
                onValueChange={(value) => setValue("role_id", value === "none" ? "" : value)}
              >
                <SelectTrigger id="role" className="w-full">
                  <SelectValue placeholder={t("usuarios.selectRole")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("usuarios.noRole")}</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sucursales (multi-select) */}
            <div className="space-y-2">
              <Label>{t("usuarios.fields.sucursal")}</Label>
              {isAdminGlobal ? (
                <div className="flex items-center h-10 px-3 border rounded-md bg-muted/50">
                  <Badge variant="secondary">{t("usuarios.allSucursales")}</Badge>
                </div>
              ) : isSucursalLocked && !isEditing ? (
                <div className="flex items-center h-10 px-3 border rounded-md bg-muted/50 text-sm">
                  <span className="font-medium">{sucursalActiva?.nombre}</span>
                </div>
              ) : (
                <Popover open={sucursalPopoverOpen} onOpenChange={setSucursalPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={sucursalPopoverOpen}
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {selectedSucursalIds.length === 0
                          ? t("usuarios.selectSucursales")
                          : t("usuarios.sucursalesSelected", { count: selectedSucursalIds.length })}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                    <div className="max-h-60 overflow-y-auto space-y-1">
                      {sucursales.map((s) => {
                        const isChecked = selectedSucursalIds.includes(String(s.id));
                        return (
                          <label
                            key={s.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-sm"
                          >
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleSucursal(String(s.id))}
                            />
                            <span>{s.nombre}</span>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {/* Show selected sucursales as badges */}
              {!isAdminGlobal && selectedSucursalNames.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedSucursalNames.map((name) => (
                    <Badge key={name} variant="outline" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
